import { request } from '../client'
import type { OperatorRole, Permission } from '../../access/permissions'
import type { NavBadgeKey } from '../../../app/router/nav'
import { auditCsv, getAuditPage, type AuditFilters, type AuditPage } from '../platform'
import type { ConsoleAuth } from './platform'

/**
 * Identity & access — the operator Team (K11), its duty matrix, the Activity
 * log (K12) and the sidebar's count pills. Owned by PKG-ACCESS.
 *
 * Moved out of `./platform.ts` (MASTER-PLAN §3 F7), which re-exports every
 * name so existing imports keep working. Paths are relative to `/api/v1`; the
 * server enforces every permission.
 */

const enc = encodeURIComponent

const get = <T>(path: string, auth: ConsoleAuth): Promise<T> =>
  request<T>(path, { context: 'user', token: auth.token, signal: auth.signal })

const send = <T>(method: 'POST' | 'PATCH' | 'PUT', path: string, body: unknown, auth: ConsoleAuth): Promise<T> =>
  request<T>(path, { method, body, context: 'user', token: auth.token, signal: auth.signal })

/* ── Team (K11) ──────────────────────────────────────────────────────────── */

export type TeamMemberStatus = 'ACTIVE' | 'INVITED' | 'DEACTIVATED'

export interface TeamMember {
  userId: string
  name: string | null
  email: string | null
  operatorRole: OperatorRole
  isActive: boolean
  status: TeamMemberStatus
  /** A confirmed authenticator-app second factor. */
  twoStep: boolean
  /** P3-17 — whether this person's duty must use two-step login under the current rule. */
  twoStepRequired: boolean
  isYou: boolean
  lastLoginAt: string | null
  /** P2-04 — "Last active": refreshed with every token pair (≤ 15 min granularity). */
  lastSeenAt: string | null
  createdAt: string
}

export interface DutyPermission {
  key: Permission
  label: string
  detail: string
}

/** The RBAC table, read from the server — never restated in the frontend. */
export interface DutyMatrix {
  permissions: DutyPermission[]
  duties: { role: OperatorRole; permissions: Permission[] }[]
}

/**
 * P1-12 — "Invite teammate". No password: the colleague chooses their own from
 * an emailed single-use link (72 hours).
 */
export interface InviteOperatorBody {
  email: string
  name: string
  operatorRole: OperatorRole
}

/** @deprecated The old "Add teammate" name; the body no longer carries a password. */
export type AddOperatorBody = InviteOperatorBody

/** What inviting (or re-inviting) answers. `link` only when the email did not go. */
export interface OperatorInvitation {
  member: TeamMember
  delivery: 'EMAIL' | 'LINK'
  /** Absolute, or app-relative (`#/invite/…`) when the server has no base URL. */
  link: string | null
  expiresAt: string
}

/** P3-17 / D19 — which duties must use two-step login. */
export interface TeamSecurity {
  requiredFor: OperatorRole[]
  /** Never optional (OWNER, ADMIN, FINANCE). */
  alwaysRequired: OperatorRole[]
}

export const getTeam = (auth: ConsoleAuth): Promise<TeamMember[]> => get('/superadmin/team', auth)

export const getDuties = (auth: ConsoleAuth): Promise<DutyMatrix> => get('/superadmin/console/platform/duties', auth)

/** Owners only; never your own role; never the last owner. 400 carries `code`. */
export const setOperatorRole = (userId: string, operatorRole: OperatorRole, auth: ConsoleAuth): Promise<TeamMember> =>
  send('PATCH', `/superadmin/team/${enc(userId)}/role`, { operatorRole }, auth)

/** Invites the operator with their duty in one write; they set their own password. */
export const inviteOperator = (body: InviteOperatorBody, auth: ConsoleAuth): Promise<OperatorInvitation> =>
  send('POST', '/superadmin/team', body, auth)

/** @deprecated Kept for the `./platform` re-export; use `inviteOperator`. */
export const addOperator = inviteOperator

/** A fresh link for an operator who never set a password (the old link dies). 409 `ALREADY_CLAIMED`. */
export const resendOperatorInvite = (userId: string, auth: ConsoleAuth): Promise<OperatorInvitation> =>
  send('POST', `/superadmin/team/${enc(userId)}/invite`, {}, auth)

export const getTeamSecurity = (auth: ConsoleAuth): Promise<TeamSecurity> => get('/superadmin/team/security', auth)

/** Owners only. OWNER / ADMIN / FINANCE are always put back by the server. */
export const setTeamSecurity = (requiredFor: OperatorRole[], auth: ConsoleAuth): Promise<TeamSecurity> =>
  send('PUT', '/superadmin/team/security', { requiredFor }, auth)

/** Never yourself, never the last active operator; signs them out everywhere. */
export const deactivateOperator = (userId: string, auth: ConsoleAuth): Promise<{ revokedSessions: number }> =>
  send('POST', `/superadmin/operators/${enc(userId)}/deactivate`, {}, auth)

/* ── Activity log (K12) ──────────────────────────────────────────────────── */

export type { AuditRow, AuditPage, AuditFilters, FilterableAuditAction } from '../platform'
export { FILTERABLE_AUDIT_ACTIONS } from '../platform'

/** Newest first, cursor-paged (≤ 200 a page), with `total` and server `q`. The read is itself audited. */
export const getAudit = (filters: AuditFilters, auth: ConsoleAuth): Promise<AuditPage> => getAuditPage(filters, auth)

/** Every row matching the filters as CSV (streamed; ids only; audited as a data export). */
export const getAuditCsv = (filters: AuditFilters, auth: ConsoleAuth): Promise<string> => auditCsv(filters, auth)

/** P2-05 — one person the Who filter can offer: staff or an operator, never a parent. */
export interface AuditActor {
  userId: string
  name: string | null
  email: string | null
  role: string
  /** An operator's duty; null for school staff. */
  operatorRole: OperatorRole | null
  /** A school-staff member's school; null for operators. */
  schoolName: string | null
}

/** `GET /audit/actors?q=` — at most 20 people whose name or email contains `q` (2+ characters). */
export const getAuditActors = (q: string, auth: ConsoleAuth): Promise<AuditActor[]> =>
  get(`/audit/actors?q=${enc(q)}`, auth)

/* ── Sidebar count pills (F5) ────────────────────────────────────────────── */

/** `GET /me/nav-badges` — a key is absent when nothing is waiting or the list is not yours. */
export type NavBadgeCounts = Partial<Record<NavBadgeKey, number>>

/** Every count pill in one request. Polled by `useNavBadges`; errors are the caller's to swallow. */
export const getNavBadges = (auth: ConsoleAuth): Promise<NavBadgeCounts> => get('/me/nav-badges', auth)
